/*
 * ST 2026-10-09 PDP 3D viewer (Wrike 4534349010) — markup in snippets/st-3d-viewer.liquid.
 * ST 2026-10-11 round 5: inline in the first gallery media instead of a modal (Ali, Technogym reference). The "3D"
 * button creates the <model-viewer> from its <template> in the stage over the photo and imports the bundled module
 * (assets/st-model-viewer.min.js, which imports st-three.module.min.js and st-three.core.min.js), so the PDP carries
 * no 3D payload on load. The "X" hides the stage again; the viewer is kept, so a second tap shows it at once.
 * Pointer, touch, wheel and click events stop at the stage, so rotating never moves the gallery slider and never opens
 * the image zoom. The stage's touch-action pan-y (assets/st-3d-viewer.css) keeps the native slider scroll from taking
 * horizontal drags; vertical swipes still scroll the page.
 * Load chain: hover/focus on the button warms up the modules and the self-hosted Draco decoder, pointerdown/click
 * also fetches the GLB, all in parallel. model-viewer then reads them from the HTTP cache.
 * Round 6: where model-viewer offers no AR once the model is loaded and the device has a mouse (desktop), the
 * "open on your smartphone" note replaces the AR button.
 * Round 7: the AR button sits in the bottom row next to "3D" (no longer inside model-viewer), so iPhone and Android
 * users see it without opening 3D first. Before the module is loaded, AR support is predicted with model-viewer 4.3.1's
 * own rules (AR_PLATFORM); once the model is loaded, model-viewer's canActivateAR decides. A tap on it before the model
 * is ready opens the 3D view and starts AR on load; when the model is ready, the tap starts AR right away. Where an
 * iPhone browser has no Quick Look (needs ios-src outside Safari, never in the Google app), the "open in Safari" note
 * replaces the button.
 * Round 8 (Ali: no "Safari only" note on live): iPhone browsers without Quick Look get no AR button and no note; the
 * button shows only where model-viewer will offer AR (Android, iPhone Safari, other iPhone browsers with ios-src).
 */
(() => {
  if (customElements.get("st-3d-viewer")) return;

  const ISOLATED_EVENTS = [
    "pointerdown",
    "pointermove",
    "pointerup",
    "mousedown",
    "touchstart",
    "touchmove",
    "touchend",
    "wheel",
    "click",
    "keydown",
  ];
  const DECODER_FILES = ["draco_wasm_wrapper.js", "draco_decoder.wasm"];
  const warmed = new Set();
  const DESKTOP = "(hover: hover) and (pointer: fine)";
  const UA = navigator.userAgent;
  const IOS =
    /iPad|iPhone|iPod/.test(UA) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // Same checks as model-viewer 4.3.1 (scene-viewer and quick-look modes).
  const IOS_OTHER_BROWSER =
    IOS && /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(UA);
  const IOS_QUICK_LOOK =
    IOS && document.createElement("a").relList?.supports?.("ar");
  const SCENE_VIEWER =
    /android/i.test(UA) && !/firefox/i.test(UA) && !/OculusBrowser/.test(UA);
  const AR_PLATFORM = IOS || SCENE_VIEWER;
  let moduleLoad = null;

  // Same request mode as three's FileLoader (cors, credentials same-origin), so the cached response is reused.
  const prefetch = (url) => {
    if (!url || warmed.has(url)) return;
    warmed.add(url);
    fetch(url, { credentials: "same-origin" })
      .then((response) => response.arrayBuffer())
      .catch(() => warmed.delete(url));
  };

  const modulePreload = (url) => {
    if (!url || warmed.has(url)) return;
    warmed.add(url);
    const link = document.createElement("link");
    link.rel = "modulepreload";
    link.href = url;
    document.head.append(link);
  };

  class St3dViewer extends HTMLElement {
    connectedCallback() {
      if (this.trigger) return;

      this.trigger = this.querySelector(".st-3d-viewer__trigger");
      this.closeButton = this.querySelector(".st-3d-viewer__close");
      this.stage = this.querySelector(".st-3d-viewer__stage");
      this.arButton = this.querySelector(".st-3d-viewer__ar");
      if (!this.trigger || !this.stage) return;

      if (this.arButton && this.arExpected) {
        this.arButton.hidden = false;
        this.arButton.addEventListener("pointerdown", () => this.warmUp(true), {
          passive: true,
        });
        this.arButton.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.startAR();
        });
      }

      this.trigger.addEventListener("pointerenter", () => this.warmUp(), {
        passive: true,
      });
      this.trigger.addEventListener("focus", () => this.warmUp());
      this.trigger.addEventListener("pointerdown", () => this.warmUp(true), {
        passive: true,
      });
      this.trigger.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      });
      this.closeButton?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });
      ISOLATED_EVENTS.forEach((type) => {
        this.stage.addEventListener(type, (event) => event.stopPropagation(), {
          passive: true,
        });
      });
      this.stage.addEventListener("keydown", (event) => {
        if (event.key === "Escape") this.close();
      });
    }

    warmUp(withModel = false) {
      const { decoder, moduleDeps = "", src } = this.dataset;
      if (decoder) {
        // Read once by model-viewer when its module is evaluated, so it has to be set before the import.
        self.ModelViewerElement = {
          ...self.ModelViewerElement,
          dracoDecoderLocation: decoder,
        };
        DECODER_FILES.forEach((file) => prefetch(decoder + file));
      }
      moduleDeps.split(",").forEach(modulePreload);
      if (withModel) prefetch(src);

      if (!moduleLoad) {
        moduleLoad = import(this.dataset.module).catch((error) => {
          moduleLoad = null;
          console.error("st-3d-viewer: model-viewer failed to load", error);
        });
      }
    }

    open() {
      const template = this.stage.querySelector("template");
      if (template) {
        this.stage.append(template.content.cloneNode(true));
        template.remove();
        this.watchAR();
      }

      this.warmUp(true);
      this.stage.hidden = false;
      if (this.closeButton) this.closeButton.hidden = false;
      this.trigger.setAttribute("aria-pressed", "true");
      this.classList.add("is-open");
    }

    startAR() {
      const model = this.stage.querySelector("model-viewer");
      if (this.loaded && model) {
        if (model.canActivateAR) model.activateAR();
        else this.open();
        return;
      }
      this.arPending = true;
      this.open();
    }

    get arExpected() {
      return SCENE_VIEWER || (IOS && !this.iosWithoutQuickLook);
    }

    // Quick Look outside Safari needs ios-src; the Google app never gets it (model-viewer 4.3.1).
    get iosWithoutQuickLook() {
      if (!IOS || /GSA\//.test(UA)) return IOS;
      return IOS_OTHER_BROWSER ? !("iosSrc" in this.dataset) : !IOS_QUICK_LOOK;
    }

    watchAR() {
      const model = this.stage.querySelector("model-viewer");
      if (!model) return;
      model.addEventListener(
        "load",
        () => {
          this.loaded = true;
          const canAR = model.canActivateAR;
          const hint =
            !canAR && !AR_PLATFORM && matchMedia(DESKTOP).matches && "phone";
          this.stage
            .querySelectorAll(".st-3d-viewer__ar-hint")
            .forEach((note) => {
              note.hidden = note.dataset.hint !== hint;
            });
          if (this.arButton) this.arButton.hidden = !canAR;
          if (this.arPending && canAR && !this.stage.hidden) model.activateAR();
          this.arPending = false;
        },
        { once: true },
      );
    }

    close() {
      if (this.stage.hidden) return;
      const hadFocus = this.contains(document.activeElement);
      this.stage.hidden = true;
      if (this.closeButton) this.closeButton.hidden = true;
      this.trigger.setAttribute("aria-pressed", "false");
      this.classList.remove("is-open");
      this.arPending = false;
      if (hadFocus) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define("st-3d-viewer", St3dViewer);
})();
